<script setup lang="ts">
import { Head, Link, useForm } from '@inertiajs/vue3'
import { onMounted, ref, watch } from 'vue'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import { InputNumber, Select } from 'primevue'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import Icon from '@/Components/Utils/Icon.vue'
import PureSelectInfiniteScroll from '@/Components/Pure/PureSelectInfiniteScroll.vue'

interface BrandOption {
    id: number
    label: string
    customer_name?: string | null
    status?: string
    status_icon?: any
    products_count?: number
}

interface ProductOption {
    id: number
    label: string
    sku: string | null
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    initialBrand: BrandOption | null
    submitRoute: { name: string }
}>()

const form = useForm({
    brand_id: props.initialBrand?.id ?? null as number | null,
    brand_product_id: null as number | null,
    initial_quantity: null as number | null,
})

const selectedBrand = ref<BrandOption | null>(props.initialBrand)
const products = ref<ProductOption[]>([])
const isLoadingProducts = ref(false)
let latestProductsRequest = 0

const loadProducts = async () => {
    form.brand_product_id = null
    products.value = []
    if (!form.brand_id) return

    const requestId = ++latestProductsRequest
    isLoadingProducts.value = true

    try {
        const response = await axios.get(route('kasir.stocks.brand-products', form.brand_id))
        if (requestId !== latestProductsRequest) return

        products.value = response.data.data
        form.brand_product_id = products.value[0]?.id ?? null
    } catch (error) {
        if (requestId !== latestProductsRequest) return
        notify({ title: 'Gagal memuat produk brand', text: 'Coba pilih ulang brand.', type: 'error' })
    } finally {
        if (requestId === latestProductsRequest) isLoadingProducts.value = false
    }
}

onMounted(loadProducts)

watch(() => form.brand_id, () => {
    form.clearErrors('brand_product_id')
    loadProducts()
})

const submit = () => {
    form.post(route(props.submitRoute.name))
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="mx-auto max-w-2xl px-4 py-6">
        <section class="rounded-xl border border-gray-200 bg-white p-6">
            <p class="mb-4 text-sm text-gray-500">
                Pilih produk brand yang akan memakai stok. Stok ini hanya bisa dipakai oleh brand tersebut
                (atau dipindahkan lewat transfer stok).
            </p>

            <div class="grid gap-4">
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Brand <span class="text-red-500">*</span></label>
                    <PureSelectInfiniteScroll
                        v-model="form.brand_id"
                        :fetchRoute="{ name: 'kasir.brands.options' }"
                        optionLabel="label"
                        optionValue="id"
                        :initialOption="initialBrand"
                        searchPlaceholder="Cari brand atau pelanggan"
                        placeholder="Pilih brand"
                        emptyText="Brand tidak ditemukan"
                        @change="brand => selectedBrand = brand as BrandOption | null"
                    >
                        <template #value="{ placeholder }">
                            <span v-if="selectedBrand" class="flex items-center gap-x-1.5">
                                {{ selectedBrand.label }}
                                <Icon v-if="selectedBrand.status === 'eksklusif'" :data="selectedBrand.status_icon" />
                            </span>
                            <span v-else>{{ placeholder }}</span>
                        </template>
                        <template #option="{ option }">
                            <span class="flex w-full items-center justify-between gap-x-3">
                                <span class="min-w-0">
                                    <span class="flex items-center gap-x-1.5">
                                        {{ option.label }}
                                        <Icon v-if="option.status === 'eksklusif'" :data="option.status_icon" />
                                    </span>
                                    <span v-if="option.customer_name" class="block truncate text-xs text-gray-400">{{ option.customer_name }}</span>
                                </span>
                                <span class="shrink-0 text-xs" :class="option.products_count ? 'text-gray-500' : 'text-gray-300'">
                                    {{ option.products_count ?? 0 }} produk
                                </span>
                            </span>
                        </template>
                    </PureSelectInfiniteScroll>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Produk brand <span class="text-red-500">*</span></label>
                    <Select
                        v-model="form.brand_product_id"
                        :options="products"
                        optionLabel="label"
                        optionValue="id"
                        :placeholder="isLoadingProducts ? 'Memuat produk...' : 'Pilih produk'"
                        :loading="isLoadingProducts"
                        :disabled="isLoadingProducts || !products.length"
                        :invalid="!!form.errors.brand_product_id"
                        filter
                        filterPlaceholder="Cari produk"
                        fluid
                        @change="form.clearErrors('brand_product_id')"
                    >
                        <template #option="{ option }">
                            <span class="flex w-full items-center justify-between gap-x-3">
                                <span>{{ option.label }}</span>
                                <span v-if="option.sku" class="shrink-0 font-mono text-xs text-gray-400">{{ option.sku }}</span>
                            </span>
                        </template>
                    </Select>
                    <div v-if="selectedBrand && !isLoadingProducts && !products.length" class="mt-1 text-xs text-gray-400">
                        Semua produk brand ini sudah pakai stok, atau brand belum punya produk.
                        <Link :href="route('kasir.brands.products.create', selectedBrand.id)" class="secondaryLink">
                            Tambah produk brand
                        </Link>
                    </div>
                    <div v-if="form.errors.brand_product_id" class="mt-1 text-xs text-red-500">{{ form.errors.brand_product_id }}</div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Stok awal</label>
                    <InputNumber
                        v-model="form.initial_quantity"
                        :min="0"
                        locale="id-ID"
                        placeholder="0"
                        suffix=" pcs"
                        fluid
                        :invalid="!!form.errors.initial_quantity"
                        @update:modelValue="form.clearErrors('initial_quantity')"
                    />
                    <div class="mt-1 text-xs text-gray-400">Opsional. Dicatat sebagai stok opname "Stok awal", bisa diubah nanti di halaman stok.</div>
                    <div v-if="form.errors.initial_quantity" class="mt-1 text-xs text-red-500">{{ form.errors.initial_quantity }}</div>
                </div>
            </div>
        </section>

        <div class="mt-4 flex justify-end">
            <Button
                type="save"
                label="Simpan"
                :loading="form.processing"
                :disabled="!form.brand_product_id"
                v-tooltip="'Ubah produk ini menjadi sistem stok brand, lalu buka halaman stoknya'"
                @click="submit"
            />
        </div>
    </div>
</template>
